// components/FloatingWidget/FloatingWidget.jsx  (WEBSITE ONLY)
// Applies the live-site rules (enabled, routes, schedule, reveal delay, page
// context, analytics) and renders FloaterView. The admin editor doesn't use this.
'use client'

import { useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import FloaterView from './FloaterView'
import { useFloaterPage } from './FloatingWidgetContext'
import { resolveConfig, stripLocale, isRouteVisible, isOpenNow, resolveTarget } from './floaterUtils'

export default function FloatingWidget({ config, lang = 'en', defaultLocale = 'en', locales = [] }) {
  const cfg = useMemo(() => resolveConfig(config), [config])
  const pathname = usePathname() || '/'
  const page = useFloaterPage()

  const [mounted, setMounted] = useState(false)
  const [revealed, setRevealed] = useState(false)
  useEffect(() => setMounted(true), [])

  // Reveal after delay / scroll
  const { showAfterMs, showAfterScrollPx } = cfg.visibility
  useEffect(() => {
    if (!showAfterMs && !showAfterScrollPx) { setRevealed(true); return }
    const reveal = () => setRevealed(true)
    const t = showAfterMs ? setTimeout(reveal, showAfterMs) : null
    const onScroll = () => { if (window.scrollY >= showAfterScrollPx) reveal() }
    if (showAfterScrollPx) { window.addEventListener('scroll', onScroll, { passive: true }); onScroll() }
    return () => { clearTimeout(t); window.removeEventListener('scroll', onScroll) }
  }, [showAfterMs, showAfterScrollPx])

  // Client-only filtering (schedule depends on the visitor's clock)
  const channels = useMemo(() => {
    if (!mounted) return []
    return cfg.channels.filter(c =>
      c.enabled &&
      (resolveTarget(c, page.targets) || (c.type === 'wechat' && c.qrImage)) &&
      isOpenNow(c.schedule),
    )
  }, [cfg.channels, page.targets, mounted])

  const path = stripLocale(pathname, locales)
  const visible =
    mounted && revealed && cfg.enabled && !page.hide && channels.length > 0 &&
    isRouteVisible(path, cfg.visibility.routeMode, cfg.visibility.routes)

  if (!visible) return null

  return (
    <FloaterView
      config={cfg}
      channels={channels}
      lang={lang}
      defaultLocale={defaultLocale}
      targets={page.targets}
      closeKey={pathname}
      getVars={() => ({
        url: window.location.href,
        path: pathname,
        title: document.title,
        ...page.vars,
      })}
      onItemClick={c => {
        try {
          ;(window.dataLayer = window.dataLayer || []).push({
            event: cfg.analytics.event,
            floater_channel: c.type,
            floater_id: c.id,
            floater_path: path,
          })
        } catch {}
      }}
    />
  )
}
